import {Box, Button} from '@mui/material';
import {EMPTY_ID, useGetInventoryDataStats, useGetPlan, useGetPlanSettings} from 'hooks/swr';
import useLanguage from 'hooks/useLanguage';
import useParseURL from 'hooks/useParseURL';
import {useTranslation} from 'react-i18next';
import {NoMaxWidthTooltip} from './Generic/NoMaxWidthTooltip';
import UpdateIcon from '@mui/icons-material/Update';
import * as React from 'react';
import {isInProgressState, usePlanOptimization} from 'hooks/usePlanOptimization';
import {type PlanSettings, type OptimizationRun, type Plan, type ProductionHistoryStats} from '../client';
import {dateReader} from '../helpers/UnitsFormatHandler';
import {add} from 'date-fns';
import {URLPathname} from '../interfaces';

const PlanSynchronizeButton = () => {
  const {t} = useTranslation('common');
  const {dateFormatter} = useLanguage();
  const {planId, navigate, search} = useParseURL();
  const {data: plan} = useGetPlan({resourceId: planId});
  const {data: inventoryStats} = useGetInventoryDataStats({resourceId: EMPTY_ID});
  const {optimizationRun} = usePlanOptimization({dontEnqueueSnackbar: true});
  const {data: planSettings} = useGetPlanSettings({resourceId: planId});
  const lastSynced = plan?.lastSynced
    ? `${t('lastSynchronized')}: ${dateFormatter.toLimitedTimeAgo(new Date(plan.lastSynced))}`
    : '';

  const isSynchronizable: boolean =
    planSettings && plan && inventoryStats
      ? calculateIsSynchronizable({optimizationRun, plan, inventoryStats, planSettings})
      : false;

  return (
    <React.Fragment>
      <NoMaxWidthTooltip
        title={
          <div>
            {isSynchronizable ? t('syncPlanTooltip') : t('cantSynchronizeTooltip')}
            {lastSynced && <br />}
            {lastSynced}
          </div>
        }
        placement="right"
      >
        <Box sx={{width: '100%'}}>
          <Button
            fullWidth
            disabled={!isSynchronizable}
            size="small"
            color="success"
            variant="contained"
            onClick={() => {
              navigate({
                pathname: URLPathname.planSynchronization,
                search: search.toString(),
              });
            }}
            endIcon={<UpdateIcon fontSize="medium" />}
            id="onboarding-main-synchronization"
          >
            {t('synchronizeButtonText')}
          </Button>
        </Box>
      </NoMaxWidthTooltip>
    </React.Fragment>
  );
};
export default PlanSynchronizeButton;

export const calculateIsSynchronizable = ({
  optimizationRun,
  plan,
  inventoryStats,
  planSettings,
}: {
  optimizationRun?: OptimizationRun;
  plan: Plan;
  inventoryStats: ProductionHistoryStats;
  planSettings: PlanSettings;
}): boolean => {
  if ((optimizationRun && isInProgressState(optimizationRun.state)) ?? !plan?.active) {
    return false;
  }

  if (planSettings.shouldSynchronize) {
    return true;
  }

  if (!inventoryStats.lastDate || !inventoryStats.lastUpdated) {
    return false;
  }

  const lastPossibleSyncDate = add(dateReader(inventoryStats.lastDate), {days: 1});
  const planStartDate = dateReader(plan.startDate);
  if (planStartDate.getTime() > lastPossibleSyncDate.getTime()) {
    return false;
  }
  const planLastSynced = plan.lastSynced ? dateReader(plan.lastSynced) : undefined;
  const inventoryStatsLastUpdated = dateReader(inventoryStats.lastUpdated);

  if (planStartDate.getTime() < lastPossibleSyncDate.getTime()) {
    return true;
  }
  if (
    planStartDate.getTime() === lastPossibleSyncDate.getTime() &&
    (planLastSynced === undefined || planLastSynced.getTime() < inventoryStatsLastUpdated.getTime())
  ) {
    return true;
  }
  return false;
};
